/**
 * TUS direct-to-Bunny uploader.
 *
 * Replaces the old chunked-through-backend flow (which broke on files
 * >100 MB because Cloudflare enforces a 100 MB request body limit).
 * With TUS the client PATCHes chunks straight to Bunny's tusupload
 * endpoint — our backend + Cloudflare are never in the byte path.
 *
 * Flow orchestrated by `startTusUpload`:
 *   1. Caller has already POSTed metadata to /api/uploads/presign and
 *      received {video_guid, library_id, signature, expiration}.
 *   2. This util wraps tus-js-client with Bunny's required headers and
 *      wires progress / retry / error callbacks.
 *   3. On success, caller POSTs /api/uploads/tus-finalize with the
 *      chunk_retry_count we surface here.
 *
 * Chunk size: 15 MB. Larger than typical mobile RTT limits but well
 * under Cloudflare's 100 MB ceiling — even if a chunk somehow does route
 * through Cloudflare (it shouldn't, TUS goes direct to Bunny), it would
 * still be within limits.
 *
 * Retry delays: 0s (instant retry once), 3s, 5s, 10s, 20s — five attempts
 * with exponential-ish backoff. Bunny is highly reliable so the last
 * 20s wait handles rare regional network hiccups.
 */

import * as tus from 'tus-js-client';

/**
 * Start a resumable direct-to-Bunny TUS upload.
 *
 * @param {object}   args
 * @param {File|Blob} args.file             The video File to upload
 * @param {object}    args.creds            Response from POST /api/uploads/presign
 *                                          (video_guid, library_id, tus_endpoint,
 *                                          signature, expiration, video_id)
 * @param {(pct:number, sent:number, total:number)=>void}  args.onProgress
 * @param {(newRetryCount:number)=>void=}  args.onRetry
 *                                          Fired on every chunk retry so caller
 *                                          can surface a subtle "reconnecting…"
 *                                          hint in the UI.
 * @param {()=>void=}                       args.onSuccess
 * @param {(err:Error, retryCount:number)=>void=}          args.onError
 *
 * @returns {{ abort: ()=>void, getRetryCount: ()=>number }}
 *          — `abort()` cancels the upload cleanly. `getRetryCount()` returns
 *          the number of times a chunk was retried (0 on a fully-clean run).
 */
export function startTusUpload({
  file,
  creds,
  onProgress,
  onRetry,
  onSuccess,
  onError,
}) {
  if (!file) throw new Error('startTusUpload: file is required');
  if (!creds?.video_guid || !creds?.signature || !creds?.library_id) {
    throw new Error('startTusUpload: presign creds incomplete');
  }

  // Retry counter shared across the tus-js-client instance. onChunkComplete
  // and onError both need to see it, and tus.Upload has no built-in retry
  // count exposed to callbacks — so we track it ourselves via onShouldRetry.
  let retryCount = 0;
  // Highest byte offset we've seen in onProgress. tus-js-client's onSuccess
  // fires when Bunny returns a success status on the final PATCH, but there
  // are known cases (see tus/tus-js-client#557) where onSuccess fires even
  // though the reported Upload-Offset does not equal the file's byte length.
  // We surface `bytesSent` back to the caller so it can be POSTed to
  // tus-finalize and compared against Bunny's server-side storageSize —
  // that comparison is what actually diagnoses a truncated upload.
  let bytesSent = 0;

  const upload = new tus.Upload(file, {
    endpoint: creds.tus_endpoint || 'https://video.bunnycdn.com/tusupload',
    // 15 MB chunks. Large enough to avoid tus overhead on every network
    // roundtrip, small enough that a lost chunk is cheap to re-send.
    chunkSize: 15 * 1024 * 1024,
    // Bunny's required auth headers per their TUS integration docs.
    // These are per-video credentials produced by our /uploads/presign
    // endpoint — the raw API key is NEVER on the client.
    headers: {
      AuthorizationSignature: creds.signature,
      AuthorizationExpire: String(creds.expiration),
      VideoId: creds.video_guid,
      LibraryId: String(creds.library_id),
    },
    // metadata Bunny stores against the video. `filetype` helps Bunny
    // pick a decoder; `filename` shows in Bunny's dashboard.
    metadata: {
      filetype: file.type || 'video/mp4',
      filename: file.name || `upload-${Date.now()}.mp4`,
      // Correlate the Bunny video back to our audivue row without
      // additional lookups if we ever need to reconcile.
      audivueVideoId: creds.video_id || '',
    },
    // tus-js-client's built-in retry policy. Each entry is a delay in ms
    // before the next attempt on a network / 5xx failure.
    retryDelays: [0, 3000, 5000, 10000, 20000],
    // Persist resume state in the browser (IndexedDB) so a mid-upload
    // reload or connection drop can resume from the last completed chunk
    // instead of starting over.
    storeFingerprintForResuming: true,
    removeFingerprintOnSuccess: true,
    // onShouldRetry lets us count retries + selectively give up on auth
    // errors that will never get better (401 = expired signature, 403 =
    // wrong credentials — no point burning 5 attempts on those).
    onShouldRetry: (err, retryAttempt /* , options */) => {
      const status = err?.originalResponse?.getStatus?.();
      if (status === 401 || status === 403 || status === 404) return false;
      retryCount += 1;
      if (typeof onRetry === 'function') {
        try { onRetry(retryCount); } catch (_e) { /* observer error is not fatal */ }
      }
      return true;
    },
    onProgress: (bytesUploaded, bytesTotal) => {
      if (bytesUploaded > bytesSent) bytesSent = bytesUploaded;
      if (typeof onProgress !== 'function') return;
      const pct = bytesTotal > 0 ? Math.floor((bytesUploaded / bytesTotal) * 100) : 0;
      try { onProgress(pct, bytesUploaded, bytesTotal); } catch (_e) { /* observer error is not fatal */ }
    },
    onSuccess: () => {
      // Cross-check reported bytes against the file size. If tus-js-client
      // fires onSuccess but bytesSent < file.size, we have a truncated
      // upload — Bunny will end up as Status 6 (UploadFailed). We still
      // fire the caller's onSuccess so the finalize flow runs, but the
      // finalize handler receives the low `bytesSent` and the backend
      // then compares it against Bunny's storageSize for a definitive
      // truncation verdict.
      if (bytesSent < (file.size || 0)) {
        // Log so it shows up in Sentry / prod console for triage. Don't
        // throw — the finalize step will surface the diagnosis to the UI.
        try {
          console.warn(
            `[tusUploader] onSuccess fired but bytesSent (${bytesSent}) < ` +
            `file.size (${file.size}). Upload likely truncated.`
          );
        } catch (_e) { /* logging is best-effort */ }
      }
      if (typeof onSuccess === 'function') {
        try { onSuccess(); } catch (_e) { /* observer error is not fatal */ }
      }
    },
    onError: (err) => {
      if (typeof onError === 'function') {
        try { onError(err, retryCount); } catch (_e) { /* observer error is not fatal */ }
      }
    },
  });

  // Attempt to resume from a prior interrupted session (IndexedDB) before
  // starting fresh. If none found, tus-js-client just starts new.
  upload.findPreviousUploads().then((previousUploads) => {
    if (previousUploads.length > 0) {
      // Pick the most recent one. tus-js-client returns them oldest-first
      // in some browsers, so we just take the last entry.
      upload.resumeFromPreviousUpload(previousUploads[previousUploads.length - 1]);
    }
    upload.start();
  }).catch(() => {
    // findPreviousUploads can throw on private-browsing / restricted
    // IndexedDB. Fine — just start fresh.
    upload.start();
  });

  return {
    abort: () => {
      try { upload.abort(true); } catch (_e) { /* teardown safety */ }
    },
    getRetryCount: () => retryCount,
    getBytesSent: () => bytesSent,
  };
}
